'use client';

import { pushEvent } from '../pushEvent';
import { AnalyticsPageAttributes } from '../helpers/getAnalyticsPageAttributes';
import { useEffect, useRef } from 'react';
import { getCleanDataLayerEvent } from '../helpers/getCleanDataLayerEvent';
import { useMeasureReadingIntensity } from '@/globals/analytics/readingIntensityAnalytics';
import {
	useReferrer,
	useVisitedPartners,
} from '@/globals/pageWrappers/usePageHistory';
import { useIncrementFeedbackPageCount } from '@/sections/pageFeedback/usePageFeedbackPopup';
import { usePathname } from 'next/navigation';
import { WebAPIMetadata } from '@/globals/webApiTypes/elements/WebAPIMetadata';

type Partners = WebAPIMetadata['op:partner'];

const pushPageviewEvent = ({
	analyticsPageAttributes,
	partners = [],
	visitedPartners = new Set(),
	referrer = '',
	pathname,
	colorScheme,
}: {
	analyticsPageAttributes: AnalyticsPageAttributes;
	partners: Partners;
	visitedPartners: Set<string> | undefined;
	referrer: string | undefined;
	pathname: string;
	colorScheme: 'Dark' | 'Light' | 'Unknown';
}) => {
	if (referrer !== '') pushEvent(getCleanDataLayerEvent());

	pushEvent({
		events: {
			category: 'interaction.pageview',
		},
		page: {
			...analyticsPageAttributes,
			urlPath: analyticsPageAttributes.urlPath ?? pathname,
			referrer,
		},
		user: {
			opPartnerList: [...new Set([...visitedPartners, ...partners])],
			colorScheme,
		},
	});
};

const getColorScheme = () => {
	if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
		return 'Dark';
	} else if (window.matchMedia('(prefers-color-scheme: light)').matches) {
		return 'Light';
	}
	return 'Unknown';
};

const usePushPageviewEvent = ({
	analyticsPageAttributes,
	partners,
}: {
	analyticsPageAttributes: AnalyticsPageAttributes;
	partners: Partners;
}) => {
	const savedForPageRef = useRef<string>(undefined);
	const pathname = usePathname();

	const visitedPartners = useVisitedPartners();
	const referrer = useReferrer();
	useEffect(() => {
		// Only fire page view event if the data has changed
		if (savedForPageRef.current === pathname) {
			return;
		}
		savedForPageRef.current = pathname;

		pushPageviewEvent({
			analyticsPageAttributes,
			partners,
			visitedPartners,
			referrer,
			pathname,
			colorScheme: getColorScheme(),
		});
	}, [analyticsPageAttributes, partners, pathname, referrer, visitedPartners]);
};

/**
 * Should come before other components to make sure
 * the pageview is counted before other effects
 */
export const LogPageviewEvent = ({
	analyticsPageAttributes,
	partners,
	wordCount,
	url,
}: {
	analyticsPageAttributes: AnalyticsPageAttributes;
	partners: Partners;
	wordCount: number | undefined;
	url: string;
}) => {
	useIncrementFeedbackPageCount();
	usePushPageviewEvent({ analyticsPageAttributes, partners });
	useMeasureReadingIntensity({
		urlPath: url,
		wordCount: wordCount,
	});
	return null;
};
